import * as THREE from 'three';
import {isMirrorAxisGeometryProvider} from '../../mesh/guards/isMirrorAxisGeometryProvider';
import {isSketchArcObject, isSketchLineObject} from '../../mesh/guards';
import {SketchEntitySymmetricConstraint} from '../solver/constraint/relationDefinition/SketchEntitySymmetricConstraint';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import type {
    SketchConstraintControlPointTopologyChange,
    SketchConstraintControlPointTopologyMapping,
} from '../../../types/sketch/SketchConstraintTopologyTypes';

/**
 * 将 Corner 的控制点拓扑替换映射到实体级对称关系另一侧。
 *
 * 本服务只读取纯关系定义并计算对应点，不修改实体、场景或历史；真正的点创建、挂载和
 * 端点替换由 Corner 操作上下文统一完成，从而保持同一事务边界。
 */
export class SketchSymmetricControlPointTopologyService {
    /** 热路径复用的轴方向。 */
    private readonly axisDirectionScratch = new THREE.Vector3();
    /** 热路径复用的点到轴起点向量。 */
    private readonly pointOffsetScratch = new THREE.Vector3();
    /** 热路径复用的投影点。 */
    private readonly projectionScratch = new THREE.Vector3();

    /**
     * 解析一项控制点替换在全部实体级对称关系中的对应拓扑变化。
     * @param change 当前实体已经确定的新控制点语义。
     * @returns 对应实体、控制点索引和反射后世界坐标；无对称关系时返回空数组。
     */
    public resolveMappings(
        change: SketchConstraintControlPointTopologyChange,
    ): SketchConstraintControlPointTopologyMapping[] {
        const mappings: SketchConstraintControlPointTopologyMapping[] = [];
        change.entity.constraints.getRelations().forEach((relation) => {
            if (!(relation instanceof SketchEntitySymmetricConstraint)) {
                return;
            }
            const targets = relation.getTargetsSnapshot();
            if (targets.length !== 3) {
                throw new Error(`实体对称关系拓扑非法: relation=${relation.persistentId}`);
            }
            const [axisProvider, sourceEntity, mirroredEntity] = targets;
            const targetEntity = change.entity === sourceEntity
                ? mirroredEntity
                : change.entity === mirroredEntity
                    ? sourceEntity
                    : null;
            if (!targetEntity) {
                return;
            }
            if (!isMirrorAxisGeometryProvider(axisProvider)) {
                throw new Error(`实体对称关系缺少合法镜像轴: relation=${relation.persistentId}`);
            }
            this.assertSupportedControlPointPair(change.entity, targetEntity, change.controlPointIndex);
            const axisGeometry = axisProvider.resolveMirrorAxisGeometry();
            if (!axisGeometry) {
                throw new Error(`实体对称关系镜像轴已悬空: relation=${relation.persistentId}`);
            }
            mappings.push({
                relationId: relation.id,
                targetEntity,
                targetControlPointIndex: change.controlPointIndex,
                targetWorldPosition: this.reflectPoint(
                    change.replacementPoint.getPosition(),
                    axisGeometry.startWorld,
                    axisGeometry.endWorld,
                ),
            });
        });
        return mappings;
    }

    /**
     * 校验 Corner 当前支持的实体与控制点索引。
     * @param sourceEntity 发生替换的实体。
     * @param targetEntity 对称关系另一侧实体。
     * @param controlPointIndex 完整定义控制点索引。
     */
    private assertSupportedControlPointPair(
        sourceEntity: SketchConstraintControlPointTopologyChange['entity'],
        targetEntity: SketchConstraintControlPointTopologyMapping['targetEntity'],
        controlPointIndex: number,
    ): void {
        if (isSketchLineObject(sourceEntity) && isSketchLineObject(targetEntity)
            && (controlPointIndex === 0 || controlPointIndex === 1)) {
            return;
        }
        if (isSketchArcObject(sourceEntity) && isSketchArcObject(targetEntity)
            && (controlPointIndex === 1 || controlPointIndex === 2)) {
            return;
        }
        throw new Error(`Corner 不支持当前对称控制点拓扑: entity=${sourceEntity.uuid}, index=${controlPointIndex}`);
    }

    /**
     * 关于当前无限轴线反射世界点。
     * @param pointWorld 待反射点。
     * @param axisStartWorld 轴起点。
     * @param axisEndWorld 轴终点。
     * @returns 独立的反射点世界坐标。
     */
    private reflectPoint(
        pointWorld: THREE.Vector3,
        axisStartWorld: THREE.Vector3,
        axisEndWorld: THREE.Vector3,
    ): THREE.Vector3 {
        const axisDirection = this.axisDirectionScratch.subVectors(axisEndWorld, axisStartWorld);
        const lengthSquared = axisDirection.lengthSq();
        if (!Number.isFinite(lengthSquared)
            || lengthSquared <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared) {
            throw new Error('实体对称关系镜像轴已退化，无法传播 Corner 拓扑。');
        }
        const projectionScale = this.pointOffsetScratch.subVectors(pointWorld, axisStartWorld)
            .dot(axisDirection) / lengthSquared;
        this.projectionScratch.copy(axisStartWorld).addScaledVector(axisDirection, projectionScale);
        return this.projectionScratch.multiplyScalar(2).sub(pointWorld).clone();
    }
}
